import React from 'react';
import type { FeeStatistics } from '../../types/membershipFee';

interface Props {
  statistics: FeeStatistics;
}

const formatAmount = (amount: number) => `${amount.toLocaleString('hu-HU')} Ft`;

const FeeStatisticsPanel: React.FC<Props> = ({ statistics }) => {
  const paidPercent = statistics.totalPlayers > 0
    ? Math.round((statistics.paidCount / statistics.totalPlayers) * 100)
    : 0;

  return (
    <div className="mb-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Összesen</p>
          <p className="text-2xl font-bold text-gray-900">{statistics.totalPlayers} fő</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Fizetve</p>
          <p className="text-2xl font-bold text-green-600">{statistics.paidCount} fő</p>
          <p className="text-xs text-gray-400">{paidPercent}%</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Beszedve</p>
          <p className="text-2xl font-bold text-green-600">{formatAmount(statistics.totalCollected)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Hátralék</p>
          <p className="text-2xl font-bold text-red-600">{formatAmount(statistics.totalOutstanding)}</p>
        </div>
      </div>

      {/* Age Group Breakdown - mobile cards (below md) */}
      {statistics.byAgeGroup.length > 1 && (
        <div className="md:hidden grid grid-cols-2 gap-3">
          {statistics.byAgeGroup.map(ag => (
            <div key={ag.ageGroupName} className="bg-white rounded-lg shadow border p-3 flex flex-col">
              {/* Keskeny kártya: a "x/y fizetve" a korosztály neve alá kerül,
                  különben egy hosszabb név betűnként törne. */}
              <div className="mb-1.5 space-y-0.5">
                <div className="font-medium text-sm text-gray-900 break-words hyphens-auto leading-snug">{ag.ageGroupName}</div>
                <span className={`block whitespace-nowrap text-xs font-medium ${ag.unpaidCount === 0 ? 'text-green-600' : 'text-amber-600'}`}>
                  {ag.paidCount}/{ag.totalPlayers} fizetve
                </span>
              </div>
              <div className="space-y-0.5 text-xs text-gray-500">
                <div className="truncate">Létszám: <span className="font-medium text-gray-900">{ag.totalPlayers} fő</span></div>
                <div className="truncate">Beszedve: <span className="font-medium text-green-600">{formatAmount(ag.totalCollected)}</span></div>
                <div className="truncate">
                  Hátralék: <span className="font-medium text-red-600">{formatAmount(ag.totalExpected - ag.totalCollected)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Age Group Breakdown - desktop table (hidden below md) */}
      {statistics.byAgeGroup.length > 1 && (
        <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Korosztály</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Létszám</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Fizetve</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Beszedve</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Hátralék</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {statistics.byAgeGroup.map(ag => (
                <tr key={ag.ageGroupName}>
                  <td className="px-4 py-2 text-sm font-medium text-gray-900">{ag.ageGroupName}</td>
                  <td className="px-4 py-2 text-sm text-gray-500 text-center">{ag.totalPlayers}</td>
                  <td className="px-4 py-2 text-sm text-center">
                    <span className={`font-medium ${ag.unpaidCount === 0 ? 'text-green-600' : 'text-amber-600'}`}>
                      {ag.paidCount}/{ag.totalPlayers}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-sm text-green-600 text-right">{formatAmount(ag.totalCollected)}</td>
                  <td className="px-4 py-2 text-sm text-red-600 text-right">
                    {formatAmount(ag.totalExpected - ag.totalCollected)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FeeStatisticsPanel;
